import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sun, Moon, CaretLeft, CaretRight } from '@phosphor-icons/react';
import '../styles/App.css';

const HandIcon = ({ size = 40 }) => (
  <div
    role="img"
    aria-label="Handy Co Logo"
    style={{
      width: size,
      height: size,
      backgroundColor: 'var(--crm-text, #9CACA6)',
      WebkitMaskImage: 'url(/assets/icons/icon_handyco.svg)',
      WebkitMaskRepeat: 'no-repeat',
      WebkitMaskPosition: 'center',
      WebkitMaskSize: 'contain',
      maskImage: 'url(/assets/icons/icon_handyco.svg)',
      maskRepeat: 'no-repeat',
      maskPosition: 'center',
      maskSize: 'contain',
      flexShrink: 0,
    }}
  />
);

const DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

/**
 * Tasks page — calendar on the left, weekly task bar on the right
 */
const Tasks = () => {
  const navigate = useNavigate();

  // Auth state
  const [sessionToken, setSessionToken] = useState('');
  const [user, setUser] = useState(null);
  const [authenticated, setAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  // Data
  const [clients, setClients] = useState([]);
  const [allTasks, setAllTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Calendar state
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);

  // Week offset for the task bar (0 = current week)
  const [weekOffset, setWeekOffset] = useState(0);

  // Task modal
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [taskModalForm, setTaskModalForm] = useState({ title: '', due_date: '', client_id: '' });
  const [taskLoading, setTaskLoading] = useState(false);

  const [theme, setTheme] = useState(() => localStorage.getItem('crm_theme') || 'dark');

  const authHeaders = useMemo(
    () => (sessionToken ? { 'Authorization': `Bearer ${sessionToken}` } : {}),
    [sessionToken]
  );

  // ── Auth check ──
  useEffect(() => {
    const checkSession = async () => {
      const storedToken = localStorage.getItem('crm_session');
      const storedUser = localStorage.getItem('crm_user');

      if (!storedToken) {
        setAuthLoading(false);
        navigate('/dashboard');
        return;
      }

      try {
        const res = await fetch('/api/crm/auth/session', {
          headers: { 'Authorization': `Bearer ${storedToken}` }
        });
        const data = await res.json();

        if (res.ok && data.valid) {
          setSessionToken(storedToken);
          setUser(storedUser ? JSON.parse(storedUser) : data.user);
          setAuthenticated(true);
        } else {
          localStorage.removeItem('crm_session');
          localStorage.removeItem('crm_user');
          navigate('/dashboard');
        }
      } catch (err) {
        console.error('Session check error:', err);
        navigate('/dashboard');
      } finally {
        setAuthLoading(false);
      }
    };

    checkSession();
  }, [navigate]);

  // ── Data fetching ──
  const loadClients = useCallback(async () => {
    if (!sessionToken) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/crm/clients', { headers: authHeaders });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Unable to load clients');
      setClients(data.clients || []);
      return data.clients || [];
    } catch (err) {
      setError(err.message);
      return [];
    } finally {
      setLoading(false);
    }
  }, [sessionToken, authHeaders]);

  const loadAllTasks = useCallback(async (clientsList) => {
    if (!sessionToken || !clientsList?.length) return;
    try {
      const promises = clientsList.map(async (client) => {
        const res = await fetch(`/api/crm/clients/${client.id}/tasks`, { headers: authHeaders });
        if (!res.ok) return [];
        const data = await res.json();
        return (data.tasks || []).map(t => ({
          ...t,
          clientName: client.company || client.name || 'Unknown',
          clientId: client.id
        }));
      });
      const arrays = await Promise.all(promises);
      setAllTasks(arrays.flat());
    } catch (err) {
      console.error('Load tasks error:', err);
    }
  }, [sessionToken, authHeaders]);

  useEffect(() => {
    if (authenticated) {
      loadClients().then(cls => {
        if (cls?.length) loadAllTasks(cls);
      });
    }
  }, [authenticated, loadClients, loadAllTasks]);

  const handleRefresh = async () => {
    const cls = await loadClients();
    if (cls?.length) await loadAllTasks(cls);
  };

  // ── Date helpers ──
  const formatDateKey = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const parseDateKey = (str) => {
    if (!str) return null;
    const [y, m, d] = str.split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d, 12);
  };

  const getWeekBounds = useCallback((offset = 0) => {
    const now = new Date();
    const dow = now.getDay();
    const diffToMon = dow === 0 ? -6 : 1 - dow;
    const monday = new Date(now);
    monday.setDate(now.getDate() + diffToMon + offset * 7);
    monday.setHours(0, 0, 0, 0);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);
    return { monday, sunday };
  }, []);

  const getWeekOffsetForDate = useCallback((dateKey) => {
    const target = parseDateKey(dateKey);
    if (!target) return 0;
    const now = new Date();
    const dow = now.getDay();
    const diffToMon = dow === 0 ? -6 : 1 - dow;
    const currentMonday = new Date(now);
    currentMonday.setDate(now.getDate() + diffToMon);
    currentMonday.setHours(0, 0, 0, 0);
    const diff = target.getTime() - currentMonday.getTime();
    return Math.floor(diff / (7 * 24 * 60 * 60 * 1000));
  }, []);

  // ── Calendar data ──
  const calendarGrid = useMemo(() => {
    const year = calendarDate.getFullYear();
    const month = calendarDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startPad = (firstDay.getDay() + 6) % 7; // Monday-first

    const cells = [];
    // Pad before
    for (let i = 0; i < startPad; i++) {
      const d = new Date(year, month, -(startPad - 1 - i));
      cells.push({ date: d, key: formatDateKey(d), outside: true });
    }
    // Month days
    for (let d = 1; d <= lastDay.getDate(); d++) {
      const date = new Date(year, month, d);
      cells.push({ date, key: formatDateKey(date), outside: false });
    }
    // Pad after (fill to 42 if needed for consistent 6-row grid, or 35 for 5 rows)
    while (cells.length % 7 !== 0) {
      const d = new Date(year, month + 1, cells.length - startPad - lastDay.getDate() + 1);
      cells.push({ date: d, key: formatDateKey(d), outside: true });
    }
    return cells;
  }, [calendarDate]);

  // Task counts per date for calendar highlighting
  const taskCountByDate = useMemo(() => {
    const map = {};
    allTasks.forEach(t => {
      if (t.due_date && !t.completed) {
        map[t.due_date] = (map[t.due_date] || 0) + 1;
      }
    });
    return map;
  }, [allTasks]);

  // ── Weekly task bar ──
  const weeklyTasks = useMemo(() => {
    const { monday, sunday } = getWeekBounds(weekOffset);
    const days = [];

    for (let i = 0; i < 5; i++) {
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);
      const key = formatDateKey(date);
      days.push({
        label: WEEKDAY_LABELS[i],
        date: key,
        displayDate: `${date.getMonth() + 1}/${date.getDate()}`,
        tasks: allTasks
          .filter(t => t.due_date === key)
          .sort((a, b) => (a.completed || 0) - (b.completed || 0))
      });
    }

    const weekStart = monday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const openCount = allTasks.filter(t => {
      if (!t.due_date || t.completed) return false;
      const d = parseDateKey(t.due_date);
      return d && d >= monday && d <= sunday;
    }).length;

    return { days, weekStart, openCount, monday };
  }, [allTasks, weekOffset, getWeekBounds]);

  // Tasks for the selected calendar date
  const selectedDateTasks = useMemo(() => {
    if (!selectedDate) return null;
    return allTasks
      .filter(t => t.due_date === selectedDate)
      .sort((a, b) => (a.completed || 0) - (b.completed || 0));
  }, [allTasks, selectedDate]);

  // ── Task actions ──
  const toggleTask = async (task) => {
    try {
      const res = await fetch(`/api/crm/tasks/${task.id}`, {
        method: 'PATCH',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ completed: task.completed ? 0 : 1 })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Unable to update task');
      setAllTasks(prev => prev.map(t => t.id === task.id ? { ...t, completed: data.task.completed } : t));
    } catch (err) {
      setError(err.message);
    }
  };

  const openAddTaskModal = (defaultDate = '') => {
    setEditingTask(null);
    setTaskModalForm({ title: '', due_date: defaultDate, client_id: '' });
    setShowTaskModal(true);
  };

  const openEditTaskModal = (task) => {
    setEditingTask(task);
    setTaskModalForm({
      title: task.title || '',
      due_date: task.due_date || '',
      client_id: task.client_id || task.clientId || ''
    });
    setShowTaskModal(true);
  };

  const handleSaveTaskModal = async (e) => {
    e.preventDefault();
    if (!taskModalForm.title.trim()) return;
    setTaskLoading(true);
    setError('');

    try {
      if (editingTask) {
        const res = await fetch(`/api/crm/tasks/${editingTask.id}`, {
          method: 'PATCH',
          headers: { ...authHeaders, 'Content-Type': 'application/json' },
          body: JSON.stringify({ title: taskModalForm.title, due_date: taskModalForm.due_date || null })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Unable to update task');
        setAllTasks(prev => prev.map(t =>
          t.id === editingTask.id ? { ...t, title: data.task.title, due_date: data.task.due_date } : t
        ));
      } else {
        const clientId = taskModalForm.client_id;
        if (!clientId) { setError('Please select a client'); setTaskLoading(false); return; }

        const res = await fetch(`/api/crm/clients/${clientId}/tasks`, {
          method: 'POST',
          headers: { ...authHeaders, 'Content-Type': 'application/json' },
          body: JSON.stringify({ title: taskModalForm.title, due_date: taskModalForm.due_date || null })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Unable to create task');
        const client = clients.find(c => c.id === parseInt(clientId));
        setAllTasks(prev => [...prev, {
          ...data.task,
          clientName: client?.company || client?.name || 'Unknown',
          clientId: parseInt(clientId)
        }]);
      }
      setShowTaskModal(false);
      setTaskModalForm({ title: '', due_date: '', client_id: '' });
      setEditingTask(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setTaskLoading(false);
    }
  };

  const handleDeleteTask = async (task) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      const res = await fetch(`/api/crm/tasks/${task.id}`, { method: 'DELETE', headers: authHeaders });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Unable to delete task'); }
      setAllTasks(prev => prev.filter(t => t.id !== task.id));
      setShowTaskModal(false);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSignOut = () => {
    localStorage.removeItem('crm_session');
    localStorage.removeItem('crm_user');
    navigate('/dashboard');
  };

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    localStorage.setItem('crm_theme', next);
  };

  const todayKey = formatDateKey(new Date());

  // ── Render ──
  if (authLoading) {
    return (
      <div className={`crm-page ${theme === 'light' ? 'crm-light' : ''}`}>
        <div className="crm-login-wrapper">
          <div className="crm-login">
            <HandIcon size={80} />
            <div className="crm-auth-loading"><div className="crm-spinner" /><p>Loading...</p></div>
          </div>
        </div>
      </div>
    );
  }
  if (!authenticated) return null;

  return (
    <div className={`crm-page ${theme === 'light' ? 'crm-light' : ''}`}>
      <header className="crm-header">
        <div className="crm-header-brand" onClick={() => navigate('/dashboard')}>
          <HandIcon size={36} />
          <span className="crm-header-title"><span className="crm-header-wordmark">HANDY CO</span></span>
        </div>
        <nav className="crm-header-nav">
          <button className="crm-nav-link" onClick={() => navigate('/dashboard')}>Overview</button>
          <button className="crm-nav-link" onClick={() => navigate('/dashboard/all')}>Clients</button>
          <button className="crm-nav-link active" onClick={() => navigate('/dashboard/tasks')}>Tasks</button>
          <button className="crm-nav-link" onClick={() => navigate('/dashboard/thoughts')}>Thoughts</button>
          <button className="crm-nav-link" onClick={() => navigate('/ds')}>Design Systems</button>
        </nav>
        <div className="crm-header-actions">
          <button className="crm-button ghost" onClick={handleRefresh} disabled={loading}>
            {loading ? 'Syncing…' : 'Refresh'}
          </button>
          <button className="crm-button ghost crm-theme-toggle" onClick={toggleTheme} aria-label="Toggle theme">
            {theme === 'dark' ? <Sun weight="fill" size={18} /> : <Moon weight="fill" size={18} />}
          </button>
          {user && (
            <button className="crm-button ghost crm-user-btn" onClick={handleSignOut}>
              {user.picture && <img src={user.picture} alt="" className="crm-user-avatar" />}
              <span className="crm-user-name">{user.name?.split(' ')[0] || 'Sign out'}</span>
            </button>
          )}
        </div>
      </header>

      <main className="crm-main">
        {error && <div className="crm-error" style={{ marginBottom: 24 }}>{error}</div>}

        <div className="crm-tasks-layout">
          {/* ── Left: Calendar ── */}
          <div className="crm-tasks-calendar">
            <div className="crm-cal-header">
              <button className="crm-button ghost crm-cal-nav" onClick={() => setCalendarDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))}><CaretLeft weight="bold" size={16} /></button>
              <h3 className="crm-cal-title">
                {calendarDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </h3>
              <button className="crm-button ghost crm-cal-nav" onClick={() => setCalendarDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))}><CaretRight weight="bold" size={16} /></button>
            </div>

            <div className="crm-cal-grid">
              {DAYS_SHORT.slice(1).concat(DAYS_SHORT.slice(0, 1)).map(d => (
                <div key={d} className="crm-cal-dayname">{d}</div>
              ))}
              {calendarGrid.map((cell) => {
                const count = taskCountByDate[cell.key] || 0;
                const isToday = cell.key === todayKey;
                const isSelected = cell.key === selectedDate;
                return (
                  <button
                    key={cell.key}
                    className={[
                      'crm-cal-cell',
                      cell.outside ? 'outside' : '',
                      isToday ? 'today' : '',
                      isSelected ? 'selected' : '',
                      count > 0 ? 'has-tasks' : ''
                    ].filter(Boolean).join(' ')}
                    onClick={() => {
                      if (cell.key === selectedDate) {
                        setSelectedDate(null);
                      } else {
                        setSelectedDate(cell.key);
                        const offset = getWeekOffsetForDate(cell.key);
                        if (offset !== weekOffset) setWeekOffset(offset);
                      }
                    }}
                  >
                    <span className="crm-cal-day">{cell.date.getDate()}</span>
                    {count > 0 && <span className="crm-cal-dot" />}
                  </button>
                );
              })}
            </div>

            {/* Selected date detail */}
            {selectedDate && selectedDateTasks && (
              <div className="crm-cal-detail">
                <div className="crm-cal-detail-header">
                  <h4>{parseDateKey(selectedDate)?.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}</h4>
                  <button className="crm-button ghost" onClick={() => openAddTaskModal(selectedDate)} style={{ fontSize: 13 }}>+ Add</button>
                </div>
                {selectedDateTasks.length === 0 ? (
                  <p className="crm-muted" style={{ padding: '8px 0', fontSize: 13 }}>No tasks this day</p>
                ) : (
                  <div className="crm-cal-detail-list">
                    {selectedDateTasks.map(task => (
                      <div key={task.id} className={`crm-todos-task ${task.completed ? 'completed' : ''}`}>
                        <input
                          type="checkbox"
                          checked={!!task.completed}
                          onChange={() => toggleTask(task)}
                          className="crm-task-checkbox"
                        />
                        <div className="crm-todos-task-content" onClick={() => openEditTaskModal(task)}>
                          <span className="crm-todos-task-title">{task.title}</span>
                          <span
                            className="crm-todos-task-client"
                            onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/${task.clientId}`); }}
                          >
                            {task.clientName}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Right: Weekly task bar ── */}
          <div className="crm-tasks-weekbar">
            <div className="crm-todos-header">
              <div>
                <h2 className="crm-section-title">{weekOffset === 0 ? 'This Week' : `Week of ${weeklyTasks.weekStart}`}</h2>
                <p className="crm-section-subtitle">{weeklyTasks.openCount} open · {weeklyTasks.weekStart}</p>
              </div>
              <div className="crm-todos-header-actions">
                <button className="crm-button primary crm-todos-add-btn" onClick={() => openAddTaskModal()}>+ Add</button>
                <div className="crm-todos-nav">
                  <button className="crm-button ghost crm-todos-nav-btn" onClick={() => setWeekOffset(p => p - 1)}><CaretLeft weight="bold" size={16} /></button>
                  <button className="crm-button ghost crm-todos-nav-btn" onClick={() => setWeekOffset(0)} disabled={weekOffset === 0}>Today</button>
                  <button className="crm-button ghost crm-todos-nav-btn" onClick={() => setWeekOffset(p => p + 1)}><CaretRight weight="bold" size={16} /></button>
                </div>
              </div>
            </div>

            <div className="crm-todos-week">
              {loading ? (
                <div className="crm-todos-loading"><div className="crm-spinner" /><p>Loading tasks...</p></div>
              ) : (
                weeklyTasks.days.map(day => (
                  <div key={day.date} className={`crm-todos-day ${day.date === todayKey ? 'crm-todos-day-today' : ''}`}>
                    <div className="crm-todos-day-header">
                      <span className="crm-todos-day-label">{day.label}</span>
                      <span className="crm-todos-day-date">{day.displayDate}</span>
                      <button className="crm-todos-day-add" onClick={() => openAddTaskModal(day.date)} aria-label={`Add task for ${day.label}`}>+</button>
                    </div>
                    <div className="crm-todos-day-tasks">
                      {day.tasks.length === 0 ? (
                        <p className="crm-todos-empty">No tasks</p>
                      ) : (
                        day.tasks.map(task => (
                          <div key={task.id} className={`crm-todos-task ${task.completed ? 'completed' : ''}`}>
                            <input type="checkbox" checked={!!task.completed} onChange={() => toggleTask(task)} className="crm-task-checkbox" />
                            <div className="crm-todos-task-content" onClick={() => openEditTaskModal(task)}>
                              <span className="crm-todos-task-title">{task.title}</span>
                              <span
                                className="crm-todos-task-client"
                                onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/${task.clientId}`); }}
                              >
                                {task.clientName}
                              </span>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Task Modal */}
      {showTaskModal && (
        <div className="crm-modal-backdrop" onClick={() => setShowTaskModal(false)}>
          <div className="crm-modal crm-modal-task" onClick={(e) => e.stopPropagation()}>
            <div className="crm-card-head">
              <h3>{editingTask ? 'Edit Task' : 'New Task'}</h3>
              <button className="crm-button ghost" onClick={() => setShowTaskModal(false)}>Cancel</button>
            </div>
            <form className="crm-form" onSubmit={handleSaveTaskModal}>
              <input
                className="crm-input crm-task-input-large"
                placeholder="What needs to be done?"
                value={taskModalForm.title}
                onChange={(e) => setTaskModalForm({ ...taskModalForm, title: e.target.value })}
                autoFocus
              />
              <div className="crm-task-modal-meta">
                <div className="crm-form-field">
                  <label className="crm-form-label">Due Date</label>
                  <input
                    className="crm-input"
                    type="date"
                    value={taskModalForm.due_date}
                    onChange={(e) => setTaskModalForm({ ...taskModalForm, due_date: e.target.value })}
                  />
                  <div className="crm-task-quick-dates">
                    <button type="button" className="crm-quick-date" onClick={() => setTaskModalForm({ ...taskModalForm, due_date: todayKey })}>Today</button>
                    <button type="button" className="crm-quick-date" onClick={() => {
                      const tm = new Date(); tm.setDate(tm.getDate() + 1);
                      setTaskModalForm({ ...taskModalForm, due_date: formatDateKey(tm) });
                    }}>Tomorrow</button>
                    <button type="button" className="crm-quick-date" onClick={() => {
                      const d = new Date(); const dow = d.getDay(); const diff = dow === 0 ? 1 : 8 - dow;
                      d.setDate(d.getDate() + diff);
                      setTaskModalForm({ ...taskModalForm, due_date: formatDateKey(d) });
                    }}>Next Mon</button>
                  </div>
                </div>
                {!editingTask && (
                  <div className="crm-form-field">
                    <label className="crm-form-label">Client *</label>
                    <select
                      className="crm-select-min"
                      value={taskModalForm.client_id}
                      onChange={(e) => setTaskModalForm({ ...taskModalForm, client_id: e.target.value })}
                    >
                      <option value="">Select client...</option>
                      {clients.filter(c => c.status !== 'archived').map(c => (
                        <option key={c.id} value={c.id}>{c.company || c.name || 'Unknown'}</option>
                      ))}
                    </select>
                  </div>
                )}
                {editingTask && (
                  <div className="crm-form-field">
                    <label className="crm-form-label">Client</label>
                    <span className="crm-form-value">
                      {clients.find(c => c.id === (editingTask.client_id || editingTask.clientId))?.company ||
                       clients.find(c => c.id === (editingTask.client_id || editingTask.clientId))?.name || 'Unknown'}
                    </span>
                  </div>
                )}
              </div>
              {error && <div className="crm-error">{error}</div>}
              <div className="crm-modal-actions">
                {editingTask && (
                  <button className="crm-button crm-button-danger" type="button" onClick={() => handleDeleteTask(editingTask)}>Delete</button>
                )}
                <div className="crm-modal-actions-right">
                  <button className="crm-button ghost" type="button" onClick={() => setShowTaskModal(false)}>Cancel</button>
                  <button className="crm-button primary" type="submit" disabled={taskLoading || !taskModalForm.title.trim() || (!editingTask && !taskModalForm.client_id)}>
                    {taskLoading ? 'Saving…' : (editingTask ? 'Save' : 'Add Task')}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tasks;
